import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Pagination } from "@/components/ui/pagination";
import { cn } from "@/lib/utils";
import usePageParam from "../../hooks/usePageParam";
import { investingApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";
import { withoutBidiControls as plain } from "../snippets/bidi";
import { formatDay, whoName } from "./lib/format";
import { formatPct } from "./lib/money";
import { kindLabel, versionStatusLabel } from "./lib/target";

// The API's page size for versions (constants.py TARGET_VERSIONS_PAGE_SIZE).
const PAGE_SIZE = 50;

const STATUS_VARIANT = {
  approved: "success",
  proposed: "warning",
  draft: "outline",
  declined: "secondary",
  superseded: "outline",
};

/** Who drafted it, and who decided it, when. */
function versionMeta(row) {
  const parts = [`drafted by ${whoName(row.created_by)}${row.created_at ? ` on ${formatDay(row.created_at)}` : ""}`];
  if (row.decided_by || row.decided_at) {
    const verb = row.status === "declined" ? "declined" : "approved";
    parts.push(`${verb} by ${whoName(row.decided_by)}${row.decided_at ? ` on ${formatDay(row.decided_at)}` : ""}`);
  }
  return parts.join(" · ");
}

/**
 * The target's version history (spec §5.4), newest first, 50 a page with
 * the page in the URL (`?page=`): each version's number, status, kind,
 * title, how many companies it holds and its cash, who drafted it and who
 * decided it. A page past the end heals to page 1; only the newest page
 * lands. Reloads when `reloadTick` moves (a decision adds a row).
 */
function TargetHistory({ reloadTick }) {
  const [page, setPage] = usePageParam();
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryTick, setRetryTick] = useState(0);
  const requestRef = useRef(0);

  useEffect(() => {
    const request = ++requestRef.current;
    const isLatest = () => request === requestRef.current;
    setLoading(true);
    setError(null);
    investingApi.target.versions.list({ page }).then(
      ({ data }) => {
        if (!isLatest()) return;
        setRows(data?.results || []);
        setCount(data?.count || 0);
        setLoading(false);
      },
      (err) => {
        if (!isLatest()) return;
        if (err?.response?.status === 404 && page > 1) {
          setPage(1, { replace: true });
          return;
        }
        setError(err);
        setLoading(false);
      },
    );
  }, [page, setPage, reloadTick, retryTick]);

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  let body;
  if (error) {
    body = (
      <Alert variant="destructive" role="alert">
        <AlertDescription className="flex flex-wrap items-center gap-2">
          <span>{apiErrorMessage(error, "Couldn't load the version history.")}</span>{" "}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-11 md:h-8"
            onClick={() => setRetryTick((tick) => tick + 1)}
          >
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  } else if (loading && rows.length === 0) {
    body = (
      <div className="py-4 text-center" role="status">
        <Loader2 className="inline size-5 animate-spin text-muted-foreground" aria-hidden="true" />
        <span className="sr-only">Loading the version history…</span>
      </div>
    );
  } else if (count === 0) {
    body = <p className="m-0 text-sm">No versions yet. Each target change an agent drafts is kept here.</p>;
  } else {
    body = (
      <ul
        aria-label="Versions"
        aria-busy={loading || undefined}
        className={cn("m-0 flex list-none flex-col p-0", loading && "opacity-60")}
      >
        {rows.map((row) => (
          <li key={row.number} data-version={row.number} className="border-b border-border py-2 last:border-b-0">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="font-semibold">v{row.number}</span>{" "}
              <Badge variant={STATUS_VARIANT[row.status] || "outline"}>{versionStatusLabel(row.status)}</Badge>{" "}
              <span className="text-sm text-muted-foreground">{kindLabel(row.kind)}</span>
            </div>
            <p className="m-0 text-sm wrap-anywhere">{plain(row.title)}</p>
            <p className="m-0 text-xs text-muted-foreground">
              {row.holdings_count} {row.holdings_count === 1 ? "holding" : "holdings"} and{" "}
              {formatPct(row.cash_weight)} cash · {versionMeta(row)}
            </p>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <section aria-label="Version history" className="rounded-lg border bg-card p-4">
      <h2 className="mt-0 mb-1 text-lg font-semibold">Version history</h2>
      {count > 0 && !error && (
        <p className="mt-0 mb-2 text-sm text-muted-foreground">
          {count} {count === 1 ? "version" : "versions"}, newest first
          {totalPages > 1 ? ` — page ${page} of ${totalPages}` : ""}. Every version is kept, with who drafted it and
          who decided it.
        </p>
      )}
      {body}
      {!error && totalPages > 1 && (
        <Pagination className="mt-4" page={page} total={totalPages} onChange={(next) => setPage(next)} showEdges />
      )}
    </section>
  );
}

TargetHistory.propTypes = {
  /** Moves when the page reloads: the history is read again. */
  reloadTick: PropTypes.number.isRequired,
};

export default TargetHistory;
